import { useEffect, useState, ComponentType } from 'react';
import { useLocation } from 'wouter';
import { Loader2 } from 'lucide-react';

/**
 * Higher-Order Component that protects routes requiring authentication
 * 
 * Usage:
 *   export default withAuth(YourProtectedComponent);
 * 
 * Behavior:
 * - On mount, checks authentication via /api/auth/whoami
 * - If 401 (not authenticated), redirects to showroom (/) for browsing as guest
 * - Shows loading spinner while checking
 * - Renders children once authenticated
 */
export function withAuth<P extends object>(Component: ComponentType<P>) {
  return function AuthenticatedComponent(props: P) {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
    const [location] = useLocation();

    useEffect(() => {
      checkAuth();
    }, []);

    const checkAuth = async () => {
      try {
        const response = await fetch('/api/auth/whoami');
        
        if (response.status === 401) {
          // Not authenticated - redirect to showroom so they can browse as guest
          // Store the intended destination so they can return after login
          sessionStorage.setItem('auth_redirect', window.location.pathname + window.location.search);
          window.location.href = '/';
          return;
        }

        if (response.ok) {
          setIsAuthenticated(true);
        } else {
          // Other error - redirect to showroom
          sessionStorage.setItem('auth_redirect', window.location.pathname + window.location.search);
          window.location.href = '/';
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        // On error, redirect to showroom
        sessionStorage.setItem('auth_redirect', window.location.pathname + window.location.search);
        window.location.href = '/';
      }
    };

    // Show loading spinner while checking authentication
    if (isAuthenticated === null) {
      return (
        <div className="flex items-center justify-center min-h-screen" data-testid="auth-loading">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Checking authentication...</p>
          </div>
        </div>
      );
    }

    // Render the protected component once authenticated
    return <Component {...props} />;
  };
}
